import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, X, Loader2, Layers, FileCode, CheckSquare, Building2, Users, GitBranch, User, AlertTriangle } from 'lucide-react';
import { debounce } from 'lodash-es';
import type { AppDispatch, RootState } from '@/store';
import { globalSearch, clearSearch, setDropdownOpen, selectSearchResults, selectSearchLoading, selectSearchFacets, selectIsDropdownOpen } from '@/store/slices/searchSlice';

// Entity type icons mapping
const entityIcons: Record<string, React.ElementType> = {
  Epic: FileCode,
  Feature: CheckSquare,
  Program: Building2,
  Team: Users,
  InvestibleStructure: Layers,
  Dependency: GitBranch,
  Resource: User,
  ResourceIntakeRequest: AlertTriangle,
};

// Entity type to route mapping
const entityRoutes: Record<string, string> = {
  Epic: '/epics',
  Feature: '/features',
  Program: '/programs',
  Team: '/teams',
  InvestibleStructure: '/investible-structures',
  Dependency: '/dependencies',
  Resource: '/resources',
  ResourceIntakeRequest: '/resource-intake-requests',
};

// Entity colors for badges (theme-aware)
const entityColors: Record<string, string> = {
  Epic: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
  Feature: 'bg-green-500/20 text-green-600 dark:text-green-400',
  Program: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
  Team: 'bg-orange-500/20 text-orange-600 dark:text-orange-400',
  InvestibleStructure: 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400',
  Dependency: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
  Resource: 'bg-pink-500/20 text-pink-600 dark:text-pink-400',
  ResourceIntakeRequest: 'bg-red-500/20 text-red-600 dark:text-red-400',
};

const GlobalSearchBar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const results = useSelector(selectSearchResults);
  const isLoading = useSelector(selectSearchLoading);
  const facets = useSelector(selectSearchFacets);
  const isDropdownOpen = useSelector(selectIsDropdownOpen);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      if (searchQuery.trim().length >= 2) {
        dispatch(globalSearch({ query: searchQuery, limit: 10 }));
      }
    }, 300),
    [dispatch]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    if (value.trim().length >= 2) {
      debouncedSearch(value);
    } else {
      dispatch(clearSearch());
    }
  };

  // Handle clear
  const handleClear = () => {
    setQuery('');
    setSelectedIndex(-1);
    dispatch(clearSearch());
    inputRef.current?.focus();
  };

  // Handle result click
  const handleResultClick = (result: any) => {
    const route = entityRoutes[result.entityType];
    if (route) {
      navigate(`${route}?highlight=${result.id}`);
    }
    dispatch(setDropdownOpen(false));
    setQuery('');
  };

  // Handle view all
  const handleViewAll = () => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
    dispatch(setDropdownOpen(false));
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        handleViewAll();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        } else {
          handleViewAll();
        }
        break;
      case 'Escape':
        dispatch(setDropdownOpen(false));
        inputRef.current?.blur();
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        dispatch(setDropdownOpen(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dispatch]);

  // Group results by entity type (max 3 per type)
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.entityType]) {
      acc[result.entityType] = [];
    }
    if (acc[result.entityType].length < 3) {
      acc[result.entityType].push(result);
    }
    return acc;
  }, {} as Record<string, typeof results>);

  // Render highlight with mark tags (sanitized to prevent XSS)
  const renderHighlight = (text: string | undefined, highlights: Record<string, string[]> | undefined, field: string) => {
    if (highlights && highlights[field]?.[0]) {
      const sanitized = DOMPurify.sanitize(highlights[field][0], { ALLOWED_TAGS: ['mark', 'em', 'strong'] });
      return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
    }
    return text || '';
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim().length >= 2 && results.length > 0 && dispatch(setDropdownOpen(true))}
          placeholder="Search epics, features, programs..."
          className="w-full pl-10 pr-10 py-2 bg-slate-900/5 dark:bg-white/5 border border-slate-300/50 dark:border-white/10 rounded-lg text-sm text-slate-800 dark:text-white placeholder-slate-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/50 focus:border-[var(--accent)]/50 transition-all"
        />
        {isLoading ? (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-gray-400 animate-spin" />
        ) : query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400 hover:text-slate-700 dark:hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isDropdownOpen && (query.trim().length >= 2) && (
        <div className="absolute top-full mt-2 w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          {results.length === 0 && !isLoading ? (
            <div className="px-4 py-6 text-center text-slate-500 dark:text-gray-400 text-sm">
              No results found for "{query}"
            </div>
          ) : (
            <>
              {/* Results by entity type */}
              <div className="max-h-80 overflow-y-auto">
                {Object.entries(groupedResults).map(([entityType, typeResults]) => {
                  const Icon = entityIcons[entityType] || Layers;
                  return (
                    <div key={entityType}>
                      {/* Entity type header */}
                      <div className="px-3 py-1.5 bg-slate-100 dark:bg-white/5 text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        {entityType}s
                        <span className="ml-auto text-slate-400 dark:text-gray-500">
                          {facets[entityType] || typeResults.length}
                        </span>
                      </div>

                      {/* Results */}
                      {typeResults.map((result, idx) => {
                        const flatIndex = results.indexOf(result);
                        const isSelected = flatIndex === selectedIndex;

                        return (
                          <button
                            key={result.id}
                            onClick={() => handleResultClick(result)}
                            className={`w-full px-4 py-2.5 flex items-start gap-3 text-left hover:bg-slate-100 dark:hover:bg-white/5 transition-colors ${
                              isSelected ? 'bg-slate-100 dark:bg-white/10' : ''
                            }`}
                          >
                            <div className={`mt-0.5 p-1.5 rounded ${entityColors[entityType] || 'bg-gray-500/20 text-slate-500 dark:text-gray-400'}`}>
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                {renderHighlight(result.title, result.highlights, 'title')}
                              </div>
                              {result.subtitle && (
                                <div className="text-xs text-slate-500 dark:text-gray-400 truncate mt-0.5">
                                  {result.subtitle}
                                </div>
                              )}
                            </div>
                            {result.metadata?.status && (
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-gray-300">
                                {result.metadata.status}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* View all footer */}
              <div className="px-4 py-2.5 bg-slate-50 dark:bg-white/5 border-t border-slate-200 dark:border-white/10">
                <button
                  onClick={handleViewAll}
                  className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  View all {results.length} results →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
